import styled, { css, keyframes } from "styled-components";

const blinkA = keyframes`
  0% {
    background: rgba(255, 255, 255, 0.68);
    box-shadow: inset 0 0 0 0 rgba(37, 99, 235, 0);
  }
  40% {
    background: #dbeafe;
    box-shadow: inset 0 0 0 3px rgba(37, 99, 235, 0.45);
  }
  100% {
    background: rgba(255, 255, 255, 0.68);
    box-shadow: inset 0 0 0 0 rgba(37, 99, 235, 0);
  }
`;

const blinkB = keyframes`
  0% {
    background: rgba(255, 255, 255, 0.68);
    box-shadow: inset 0 0 0 0 rgba(59, 130, 246, 0);
  }
  40% {
    background: #bfdbfe;
    box-shadow: inset 0 0 0 3px rgba(59, 130, 246, 0.5);
  }
  100% {
    background: rgba(255, 255, 255, 0.68);
    box-shadow: inset 0 0 0 0 rgba(59, 130, 246, 0);
  }
`;

export const Container = styled.div`
  margin: 24px auto;
  width: min(1400px, calc(100% - 24px));
  border: 1px solid #d5d8de;
  border-radius: 20px;
  overflow: hidden;
  background: linear-gradient(180deg, #f8fafc 0%, #eef2f7 100%);
  box-shadow: 0 20px 35px rgba(20, 36, 61, 0.12);
  font-family: "Segoe UI", "Helvetica Neue", Arial, sans-serif;
  ${({ $compactMode, $historyOpen }) =>
    $compactMode &&
    css`
      margin: 8px auto;
      height: 90vh;
      display: flex;
      flex-direction: column;

      @media (max-width: 1100px) {
        height: ${$historyOpen ? "auto" : "90vh"};
      }

      @media (max-width: 760px) {
        height: auto;
      }
    `}
`;

export const TopBar = styled.header`
  display: flex;
  align-items: center;
  gap: 10px;
  padding: 8px 14px;
  border-bottom: 1px solid #d5d8de;
  background: linear-gradient(90deg, #f9fafb 0%, #eef3fb 100%);

  @media (max-width: 620px) {
    flex-direction: column;
    align-items: stretch;
    gap: 6px;
  }
`;

export const Header = styled.h1`
  flex: 1 1 auto;
  margin: 0;
  padding: 4px 0;
  text-align: center;
  font-size: clamp(16px, 1.6vw, 28px);
  font-weight: 700;
  color: #1f2937;
`;

export const HistoryToggle = styled.button`
  flex: 0 0 auto;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  gap: 8px;
  border: 1px solid #bfdbfe;
  border-radius: 10px;
  padding: 8px 12px;
  color: #1d4ed8;
  background: ${({ $isOpen }) => ($isOpen ? "#dbeafe" : "#ffffff")};
  font-size: 13px;
  font-weight: 800;
  cursor: pointer;
  transition: background-color 160ms ease;

  &:hover {
    background: #dbeafe;
  }

  &:focus-visible {
    outline: 3px solid rgba(37, 99, 235, 0.35);
    outline-offset: 2px;
  }
`;

export const HistoryToggleBadge = styled.span`
  min-width: 22px;
  border-radius: 999px;
  padding: 1px 6px;
  background: #1d4ed8;
  color: #ffffff;
  font-size: 12px;
  font-weight: 800;
  line-height: 18px;
`;

export const FinishBanner = styled.div`
  margin: 12px 14px 0;
  border: 1px solid #fcd34d;
  border-radius: 12px;
  background: linear-gradient(135deg, #fef3c7 0%, #fffbeb 100%);
  padding: 12px;
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 12px;
  flex-wrap: wrap;
`;

export const FinishText = styled.p`
  margin: 0;
  color: #92400e;
  font-size: 15px;
  font-weight: 700;
`;

export const FinishButton = styled.button`
  border: none;
  border-radius: 10px;
  padding: 8px 12px;
  font-size: 13px;
  font-weight: 700;
  color: #ffffff;
  background: #d97706;
  cursor: pointer;

  &:hover {
    background: #b45309;
  }
`;

export const SetupCard = styled.section`
  width: min(760px, calc(100% - 28px));
  margin: 18px auto;
  border: 1px solid #bfdbfe;
  border-radius: 14px;
  background: linear-gradient(135deg, #eff6ff 0%, #f8fbff 100%);
  padding: 18px;
`;

export const SetupTitle = styled.h2`
  margin: 0;
  color: #1e3a8a;
  font-size: clamp(18px, 1.8vw, 22px);
`;

export const SetupDescription = styled.p`
  margin: 5px 0 16px;
  color: #475569;
  font-size: 14px;
  line-height: 1.4;
`;

export const SetupGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(4, minmax(0, 1fr));
  gap: 10px;

  @media (max-width: 960px) {
    grid-template-columns: repeat(2, minmax(0, 1fr));
  }
`;

export const SetupField = styled.div`
  background: rgba(255, 255, 255, 0.85);
  border: 1px solid #dbe6f5;
  border-radius: 10px;
  padding: 10px;
  ${({ $single }) =>
    $single &&
    css`
      margin-bottom: 10px;
    `}
`;

export const SetupLabel = styled.label`
  display: block;
  margin-bottom: 6px;
  color: #334155;
  font-size: 12px;
  font-weight: 700;
`;

export const SetupInput = styled.input`
  width: 100%;
  border: 1px solid #cbd5e1;
  border-radius: 8px;
  padding: 8px 10px;
  font-size: 15px;
  color: #0f172a;

  &:focus {
    outline: none;
    border-color: #2563eb;
    box-shadow: 0 0 0 3px rgba(37, 99, 235, 0.16);
  }

  &[type="number"]::-webkit-inner-spin-button {
    -webkit-appearance: none;
  }

  &[type="number"] {
    -moz-appearance: textfield;
    appearance: textfield;
  }
`;

export const SetupToggle = styled.button`
  display: inline-flex;
  align-items: center;
  border: 0;
  padding: 5px 0;
  color: #1d4ed8;
  background: transparent;
  font-size: 13px;
  font-weight: 700;
  text-decoration: underline;
  cursor: pointer;

  &:hover {
    color: #1e3a8a;
  }

  &:focus-visible {
    outline: 3px solid rgba(37, 99, 235, 0.35);
    outline-offset: 2px;
    border-radius: 4px;
  }
`;

export const SetupActions = styled.div`
  display: flex;
  align-items: center;
  gap: 12px;
  margin-top: 8px;
  flex-wrap: wrap;
`;

export const SetupHint = styled.p`
  margin: 0;
  color: #64748b;
  font-size: 12px;
`;

export const SetupButton = styled.button`
  border: none;
  border-radius: 10px;
  padding: 10px 14px;
  font-size: 14px;
  font-weight: 700;
  color: #ffffff;
  background: linear-gradient(135deg, #2563eb 0%, #1d4ed8 100%);
  cursor: pointer;
`;

export const SetupError = styled.p`
  margin: 8px 0 0;
  color: #b91c1c;
  font-size: 13px;
  font-weight: 700;
`;

export const Workspace = styled.div`
  display: grid;
  align-items: stretch;
  grid-template-columns: ${({ $historyOpen }) =>
    $historyOpen ? "minmax(0, 1fr) minmax(0, 380px)" : "minmax(0, 1fr)"};
  ${({ $compactMode }) =>
    $compactMode &&
    css`
      flex: 1 1 auto;
      min-height: 0;
    `}

  @media (max-width: 1100px) {
    grid-template-columns: minmax(0, 1fr);
  }
`;

export const MainArea = styled.div`
  display: flex;
  flex-direction: column;
  ${({ $compactMode }) =>
    $compactMode &&
    css`
      flex: 1 1 auto;
      min-height: 0;
      height: 100%;
      display: grid;
      grid-template-columns: minmax(0, 1fr) 320px;

      @media (max-width: 1100px) {
        display: flex;
        flex-direction: column;
      }
    `}
`;

export const Board = styled.div`
  display: grid;
  min-height: 280px;
  grid-auto-rows: minmax(0, 1fr);
  grid-template-columns: repeat(6, minmax(0, 1fr));
  background: linear-gradient(180deg, #f6f7fb 0%, #f0f2f6 100%);
  ${({ $compactMode }) =>
    $compactMode &&
    css`
      min-height: 0;
      height: 100%;

      @media (max-width: 760px) {
        height: auto;
      }
    `}

  @media (max-width: 1200px) {
    grid-template-columns: repeat(3, minmax(0, 1fr));
    grid-auto-rows: minmax(124px, auto);
  }

  @media (max-width: 760px) {
    grid-auto-rows: minmax(118px, auto);
    grid-template-columns: repeat(2, minmax(0, 1fr));
  }
`;

export const CounterCard = styled.button`
  border: 0;
  border-right: 1px solid #d5d8de;
  border-bottom: 1px solid #d5d8de;
  background: ${({ $isActivated }) =>
    $isActivated ? "linear-gradient(180deg, #eaf2ff 0%, #dbeafe 100%)" : "rgba(255, 255, 255, 0.68)"};
  display: flex;
  flex-direction: column;
  justify-content: center;
  align-items: center;
  gap: 4px;
  cursor: pointer;
  min-height: 90px;
  padding: 4px 6px;
  transition: transform 180ms ease, box-shadow 180ms ease, background-color 180ms ease;
  ${({ $blinkToken }) =>
    $blinkToken > 0
      ? css`
          animation: ${$blinkToken % 2 === 0 ? blinkA : blinkB} 320ms ease;
        `
      : css`
          animation: none;
        `}
  ${({ $isLastPressed }) =>
    $isLastPressed &&
    css`
      background: rgba(219, 234, 254, 0.75);
      box-shadow: inset 0 0 0 2px rgba(37, 99, 235, 0.6);
    `}

  &:hover {
    transform: translateY(-2px);
    background: rgba(255, 255, 255, 0.9);
    box-shadow: inset 0 0 0 1px #c9d4e7;
  }

  &:active {
    transform: translateY(0);
  }

  &:disabled {
    cursor: not-allowed;
    opacity: 0.55;
    transform: none;
    box-shadow: none;
  }

  &&:focus {
    outline: none;
    background-color: inherit;
  }

  &:focus-visible {
    outline: 3px solid #3b82f6;
    outline-offset: -3px;
  }

  @media (max-width: 760px) {
    min-height: 118px;
    padding: 8px 4px;
    gap: 6px;
  }

  @media (max-width: 980px) and (min-width: 761px) {
    min-height: 124px;
    padding: 8px 6px;
    gap: 6px;
  }
`;

export const CounterLabel = styled.span`
  font-size: clamp(18px, 2vw, 42px);
  letter-spacing: 0.04em;
  line-height: 1;
  color: ${({ $isActivated }) => ($isActivated ? "#1d4ed8" : "#111827")};
  transition: color 180ms ease;

  @media (max-width: 760px) {
    font-size: 16px;
    line-height: 1.05;
  }
`;

export const CounterValue = styled.strong`
  font-size: clamp(34px, 3.8vw, 84px);
  line-height: 1;
  color: #0f172a;

  @media (max-width: 760px) {
    font-size: 30px;
    line-height: 1;
  }
`;

export const CounterKey = styled.span`
  font-size: clamp(16px, 1.7vw, 34px);
  line-height: 1;
  color: ${({ $isActivated }) => ($isActivated ? "#2563eb" : "#111827")};
  transition: color 180ms ease;

  @media (max-width: 760px) {
    font-size: 13px;
    line-height: 1;
  }
`;

export const Footer = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 8px;
  flex-wrap: wrap;
  border-top: 1px solid #d5d8de;
  padding: 8px 10px;
  background: linear-gradient(90deg, #f9fafb 0%, #eef3fb 100%);
  ${({ $compactMode }) =>
    $compactMode &&
    css`
      border-top: 0;
      border-left: 1px solid #d5d8de;
      flex-direction: column;
      align-items: flex-start;
      justify-content: flex-start;
      gap: 8px;
      padding: 8px 10px;

      @media (max-width: 1100px) {
        border-left: 0;
        border-top: 1px solid #d5d8de;
        flex-direction: row;
        align-items: center;
        justify-content: center;
      }
    `}
`;

export const CurrentCountName = styled.p`
  margin: 0;
  padding: 4px 8px;
  border: 1px solid #bfdbfe;
  border-radius: 8px;
  background: rgba(239, 246, 255, 0.9);
  font-size: clamp(13px, 1vw, 16px);
  font-weight: 700;
  color: #1e3a8a;
`;

export const Total = styled.p`
  margin: 0;
  font-size: clamp(34px, 3vw, 58px);
  font-weight: 800;
  color: #0f172a;
`;

export const Observation = styled.p`
  margin: 0;
  font-size: clamp(14px, 1.1vw, 18px);
  font-weight: 700;
  color: #334155;
`;

export const ResetButton = styled.button`
  border: none;
  border-radius: 12px;
  padding: 6px 10px;
  font-size: 15px;
  font-weight: 700;
  color: #ffffff;
  background: linear-gradient(135deg, #2563eb 0%, #1d4ed8 100%);
  cursor: pointer;
  transition: transform 180ms ease, box-shadow 180ms ease;

  &:hover {
    transform: translateY(-1px);
    box-shadow: 0 10px 20px rgba(29, 78, 216, 0.28);
  }

  &:active {
    transform: translateY(0);
  }

  &&:focus {
    outline: none;
    background: linear-gradient(135deg, #2563eb 0%, #1d4ed8 100%);
  }
`;

export const HistoryButton = styled.button`
  border: 1px solid #bfdbfe;
  border-radius: 10px;
  padding: 8px 10px;
  color: #1d4ed8;
  background: #eff6ff;
  font-size: 13px;
  font-weight: 800;
  cursor: pointer;

  &:hover {
    background: #dbeafe;
  }
`;

export const CopyButton = styled(HistoryButton)`
  color: #ffffff;
  border-color: #2563eb;
  background: #2563eb;
  font-size: 12px;

  &:hover {
    background: #1d4ed8;
  }

  &:disabled {
    cursor: not-allowed;
    opacity: 0.5;
  }
`;

export const CopyFeedback = styled.p`
  margin: 0;
  color: #166534;
  font-size: 12px;
  font-weight: 700;
`;

export const HistoryPanel = styled.aside`
  display: flex;
  flex-direction: column;
  min-height: 0;
  gap: 10px;
  border-left: 1px solid #d5d8de;
  background: #f8fafc;
  padding: 14px;
  ${({ $compactMode }) =>
    !$compactMode &&
    css`
      max-height: 78vh;
    `}

  @media (max-width: 1100px) {
    border-left: 0;
    border-top: 1px solid #d5d8de;
    max-height: none;
  }
`;

export const HistoryPanelHeader = styled.header`
  display: flex;
  align-items: flex-start;
  justify-content: space-between;
  gap: 10px;
`;

export const HistoryPanelTitle = styled.h2`
  margin: 0 0 4px;
  color: #0f172a;
  font-size: clamp(17px, 1.5vw, 20px);
`;

export const HistoryPanelClose = styled.button`
  flex: 0 0 auto;
  width: 32px;
  height: 32px;
  border: 0;
  border-radius: 8px;
  color: #334155;
  background: #e2e8f0;
  font-size: 24px;
  line-height: 1;
  cursor: pointer;

  &:hover {
    background: #cbd5e1;
  }
`;

export const HistoryToolbar = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
  flex-wrap: wrap;
`;

export const HistoryEntryList = styled.div`
  display: grid;
  align-content: start;
  gap: 10px;
  flex: 1 1 auto;
  min-height: 0;
  overflow-y: auto;
  overscroll-behavior: contain;
  padding-right: 4px;

  @media (max-width: 1100px) {
    max-height: 52vh;
  }
`;

export const HistoryEntry = styled.article`
  border: 1px solid #d7e4fa;
  border-radius: 12px;
  background: #ffffff;
  padding: 12px;
`;

export const HistoryEntryHeader = styled.div`
  display: flex;
  align-items: flex-start;
  justify-content: space-between;
  gap: 10px;
`;

export const HistoryEntryName = styled.h3`
  margin: 0 0 3px;
  color: #1e3a8a;
  font-size: 15px;
  overflow-wrap: anywhere;
`;

export const HistoryEntryMeta = styled.p`
  margin: 0;
  color: #64748b;
  font-size: 12px;
  line-height: 1.35;
`;

export const HistoryMetrics = styled.div`
  display: flex;
  gap: 8px;
  margin: 10px 0;
`;

export const HistoryMetric = styled.div`
  min-width: 88px;
  border-radius: 8px;
  padding: 6px 10px;
  background: #eff6ff;
  color: #1e3a8a;

  strong,
  span {
    display: block;
  }

  strong {
    font-size: 18px;
  }

  span {
    font-size: 11px;
    font-weight: 700;
  }
`;

export const HistoryChipList = styled.ul`
  display: flex;
  flex-wrap: wrap;
  gap: 6px;
  margin: 0 0 10px;
  padding: 0;
  list-style: none;
`;

export const HistoryChip = styled.li`
  border: 1px solid #e2e8f0;
  border-radius: 999px;
  padding: 3px 8px;
  background: #f8fafc;
  color: #334155;
  font-size: 12px;
  font-weight: 700;
`;

export const HistoryEntryActions = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: 6px;
`;

export const HistoryEntryCopyButton = styled(HistoryButton)`
  flex: 0 0 auto;
  padding: 6px 8px;
  font-size: 12px;

  &:disabled {
    cursor: not-allowed;
    opacity: 0.5;
  }
`;

export const HistoryDangerButton = styled(HistoryEntryCopyButton)`
  border-color: #fecaca;
  color: #b91c1c;
  background: #fef2f2;

  &:hover {
    background: #fee2e2;
  }
`;

export const HistoryEmpty = styled.p`
  margin: 0;
  padding: 18px;
  font-size: 13px;
  line-height: 1.5;
  border: 1px dashed #bfdbfe;
  border-radius: 10px;
  color: #475569;
  text-align: center;
`;
